import { fileUrl } from "@/v2/lib/files";
// رحلة التعلّم — نوع الجدارة ← الدورة ← الدروس ← التقدّم
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GlassCard, Chip, Skeleton, Bar, Ring } from "@/v2/components/ui";
import {
  Lock, Check, Crown, Clock, Star, Sparkles, Target, Rocket, Wrench, HeartHandshake,
  ChevronLeft, Layers, PlayCircle,
} from "lucide-react";

const ICONS = { Target, Wrench, HeartHandshake, Crown, Sparkles, Layers };
const compIcon = (n) => ICONS[n] || Target;

const STATE = {
  completed: { label: "مكتمل", tone: "emerald" },
  in_progress: { label: "قيد التنفيذ", tone: "gold" },
  available: { label: "متاح الآن", tone: "gold" },
  locked: { label: "مقفلة", tone: "muted" },
  locked_admin: { label: "مقفلة من الإدارة", tone: "muted" },
  suspended: { label: "متوقف مؤقتًا", tone: "red" },
  scheduled: { label: "قادم قريبًا", tone: "gold" },
};
const lockNote = (c) =>
  c.status === "locked" ? "مقفلة"
    : c.status === "locked_admin" ? "مقفلة من الإدارة"
      : c.status === "suspended" ? "متوقفة مؤقتًا"
        : c.status === "scheduled" ? "تُفتح في موعدها" : "";

const Node = ({ c, index, isLast, onOpen }) => {
  const locked = ["locked", "locked_admin", "scheduled", "suspended"].includes(c.status);
  const active = c.status === "available" || c.status === "in_progress";
  const st = STATE[c.status] || STATE.locked;
  return (
    <div className="flex gap-3 sm:gap-4" data-testid={`journey-node-${c.id}`}>
      <div className="relative flex flex-col items-center w-10 sm:w-12 shrink-0">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-extrabold border-2 z-10 text-sm ${
          c.status === "completed" ? "bg-ok text-ink border-ok"
            : active ? "bg-beige text-ink border-beige v2-pulse"
              : "bg-surface-2 text-ink-2 border-line"}`}>
          {c.status === "completed" ? <Check size={19} /> : locked ? <Lock size={15} /> : index + 1}
        </div>
        {!isLast && <div className="flex-1 w-[3px] rounded-full my-1"
          style={{ background: c.status === "completed" ? "#006654" : "#e3e7ea" }} />}
      </div>

      <div className={`flex-1 mb-3 ${active ? "" : ""}`}>
        {active ? (
          <GlassCard gold hover onClick={() => onOpen(c)} data-testid={`journey-current-${c.id}`}
            className="p-4 sm:p-5 cursor-pointer">
            <div className="flex items-start gap-4">
              {c.thumbnail && <img src={fileUrl(c.thumbnail)} alt="" className="hidden sm:block w-20 h-20 rounded-2xl object-cover shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <Chip tone="gold"><Rocket size={12} /> {c.status === "in_progress" ? "أكمل الآن" : "خطوتك الحالية"}</Chip>
                  <span className="text-xs text-ink-2">الدرس {index + 1}</span>
                </div>
                <h4 className="font-extrabold text-lg mb-1 truncate">{c.title}</h4>
                <p className="text-sm text-ink-2 line-clamp-2 mb-3">{c.description}</p>
                <div className="flex items-center gap-3 text-xs text-ink-2 flex-wrap">
                  <span className="flex items-center gap-1"><Clock size={13} /> {c.video_duration}</span>
                  <span className="flex items-center gap-1 v2-emerald-text font-bold"><PlayCircle size={14} /> ابدأ الدرس</span>
                </div>
              </div>
            </div>
          </GlassCard>
        ) : (
          <button onClick={() => !locked && onOpen(c)} disabled={locked} data-testid={`journey-row-${c.id}`}
            className={`w-full text-start v2-inset px-4 py-3 flex items-center gap-3 transition-colors ${
              locked ? "opacity-60 cursor-not-allowed" : "hover:border-beige/40"}`}>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm truncate">{c.title}</div>
              {!locked && <div className="text-xs text-ink-2 truncate">{c.video_duration}</div>}
            </div>
            {locked ? <Chip tone="muted"><Lock size={11} /> {lockNote(c)}</Chip> : <Chip tone={st.tone}>{st.label}</Chip>}
            {!locked && <ChevronLeft size={16} className="text-ink-2" />}
          </button>
        )}
      </div>
    </div>
  );
};

export default function Journey() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["journey", user?.id],
    queryFn: async () => (await api.get("/journey")).data,
    enabled: Boolean(user?.id),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchInterval: 15000,
  });
  const open = (c) => navigate(`/journey/${c.id}`);

  if (isLoading) return <div className="space-y-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}</div>;

  if (error) return <GlassCard className="p-8"><p className="mb-4">تعذر تحميل دوراتك.</p><button className="v2-btn-ghost" onClick={() => refetch()}>إعادة المحاولة</button></GlassCard>;

  const t = data?.totals || { total: 0, completed: 0, progress: 0 };
  const next = data?.next_step;

  return (
    <div className="space-y-7" data-testid="journey-page">
      <GlassCard gold className="p-6 sm:p-8 v2-grid v2-pattern relative overflow-hidden">
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-7">
          <Ring value={t.progress} size={128} stroke={11} id="journeyring">
            <div className="text-center">
              <div className="text-2xl font-extrabold">{t.progress}%</div>
              <div className="text-xs text-ink-2">تقدّمك</div>
            </div>
          </Ring>
          <div className="flex-1 text-center sm:text-start">
            <div className="flex items-center gap-2 justify-center sm:justify-start mb-2">
              <Chip tone="emerald"><Sparkles size={12} /> {user?.department || "دوراتي"}</Chip>
              <Chip tone="muted">{t.completed} من {t.total} درس</Chip>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold mb-2">رحلة التعلّم الخاصة بك</h1>
            {next ? (
              <p className="text-ink-2 mb-4">
                خطوتك التالية: <span className="text-ink font-bold">{next.title}</span>
                <span className="block text-xs mt-1">{next.competency} · {next.path}</span>
              </p>
            ) : (
              <p className="text-ink-2 mb-4">{t.total ? "أكملت دروسك المتاحة." : "ستظهر هنا الدورات المحددة لك في مسار."}</p>
            )}
            {next && (
              <button onClick={() => navigate(`/journey/${next.challenge_id}`)} data-testid="journey-continue" className="v2-btn">
                <Rocket size={17} /> أكمل من حيث توقفت
              </button>
            )}
          </div>
        </div>
      </GlassCard>

      {(data?.competencies || []).length === 0 && (
        <GlassCard className="p-10 text-center text-ink-2 text-sm" data-testid="journey-empty">
          لم يتم تخصيص محتوى لمسارك بعد — تواصل مع إدارة المنصة.
        </GlassCard>
      )}

      {(data?.competencies || []).map((comp, ci) => {
        const I = compIcon(comp.icon);
        return (
          <motion.section key={comp.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(ci * 0.08, 0.3) }} data-testid={`journey-comp-${comp.id}`} className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-ok/15 text-ok flex items-center justify-center shrink-0">
                <I size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg md:text-lg font-extrabold">{comp.name}</h2>
                  <Chip tone="muted">{comp.progress}%</Chip>
                </div>
                <p className="text-xs text-ink-2 truncate">{comp.desc}</p>
              </div>
            </div>

            {comp.paths.map((p) => (
              <GlassCard key={p.id} className="p-5" data-testid={`journey-path-${p.id}`}>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Layers size={17} className="text-beige-ink" />
                  <h3 className="font-extrabold">{p.name}</h3>
                  <Chip tone={p.progress === 100 ? "emerald" : "gold"}>{p.completed}/{p.total} مكتمل</Chip>
                  <div className="w-full mt-1"><Bar value={p.progress} /></div>
                  {p.desc && <p className="text-xs text-ink-2 w-full">{p.desc}</p>}
                </div>
                <div>
                  {p.challenges.length === 0 && (
                    <div className="v2-inset p-4 text-center text-sm text-ink-2" data-testid={`journey-path-empty-${p.id}`}>
                      تم تخصيص هذا المسار لك، وستُضاف دروسه قريبًا.
                    </div>
                  )}
                  {p.challenges.map((c, i) => (
                    <Node key={c.id} c={c} index={i} isLast={i === p.challenges.length - 1} onOpen={open} />
                  ))}
                </div>
              </GlassCard>
            ))}
            {comp.paths.length === 0 && (
              <GlassCard className="p-5 text-sm text-ink-2" data-testid={`journey-comp-empty-${comp.id}`}>
                تم تخصيص هذه الجدارة لك، ولا توجد مسارات منشورة بداخلها بعد.
              </GlassCard>
            )}
          </motion.section>
        );
      })}
    </div>
  );
}
